import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Grid,
  CircularProgress,
  Button,
  Dialog,
  TextField,
  Autocomplete,
  Chip,
} from "@mui/material";
import { tokens, FLAG_EMOJI_SIZE } from "@/tokens";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@/assets/svgs/edit.svg";
import DeleteIcon from "@/assets/svgs/garbage.svg";
import { 
  ScenarioCard, 
  ScenarioMetrics, 
  useDeleteScenarioMutation, 
  useUpdateScenarioMutation,
  useGetCountrySummaryQuery,
  useGetHTSCodesQuery,
} from "@/services/dashboard/sm-api";
import { getCountryFlag } from "@/constants/country-flags";
import { useNavigate } from "react-router-dom";
import DateRangeFilter from "@/components/date-range-filter";
import { useAppSelector } from "@/store";
import { roles } from "@/roles";

interface ScenarioDetailProps {
  scenario: ScenarioCard;
  metrics?: ScenarioMetrics;
  isLoadingMetrics?: boolean;
  onClose: () => void;
  onDeleted: () => void;
  onMetricsUpdate?: () => void;
  isCompact?: boolean;
  isSlim?: boolean;
  showOnlyRecommendations?: boolean;
  hideButtons?: boolean;
  isEditDialogOpen?: boolean;
  setIsEditDialogOpen?: (open: boolean) => void;
  isDeleteDialogOpen?: boolean;
  setIsDeleteDialogOpen?: (open: boolean) => void;
  id?: string;
}

const RecommendationCard = ({
  title,
  emoji,
  children,
  isCompact = false,
  isSlim = false
}: {
  title: string;
  emoji: string;
  children: React.ReactNode;
  isCompact?: boolean;
  isSlim?: boolean;
}) => (
  <Paper
    elevation={0}
    sx={{
      p: isSlim ? 1 : isCompact ? 1.5 : 4,
      borderRadius: tokens.radius.lg,
      border: `1px solid ${tokens.border.default}`,
      height: "100%",
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: isSlim ? 0.5 : isCompact ? 1 : 3 }}>
      <Typography sx={{ fontSize: isSlim ? "0.85rem" : isCompact ? "0.95rem" : "1.5rem", lineHeight: 1 }}>
        {emoji}
      </Typography>
      <Typography sx={{ fontWeight: tokens.fontWeight.bold, color: tokens.text.primary, fontSize: isSlim ? "0.85rem" : isCompact ? tokens.fontSize.lg : "1.5rem" }}>
        {title}
      </Typography>
    </Box>
    {children}
  </Paper>
);

const ScenarioDetail: React.FC<ScenarioDetailProps> = ({ 
  scenario, 
  metrics,
  isLoadingMetrics,
  onClose,
  onDeleted,
  onMetricsUpdate,
  isCompact,
  isSlim,
  showOnlyRecommendations,
  hideButtons,
  isEditDialogOpen: externalIsEditDialogOpen,
  setIsEditDialogOpen: externalSetIsEditDialogOpen,
  isDeleteDialogOpen: externalIsDeleteDialogOpen,
  setIsDeleteDialogOpen: externalSetIsDeleteDialogOpen,
  id,
}) => {
  const navigate = useNavigate();
  const { user } = useAppSelector((state: any) => state.auth);
  const { selectedClient } = useAppSelector((state: any) => state.client);
  const [localIsDeleteDialogOpen, setLocalIsDeleteDialogOpen] = useState(false);
  const [localIsEditDialogOpen, setLocalIsEditDialogOpen] = useState(false);
  
  // Use either external or local state for dialogs
  const isDeleteDialogOpen = externalIsDeleteDialogOpen !== undefined ? externalIsDeleteDialogOpen : localIsDeleteDialogOpen;
  const setIsDeleteDialogOpen = externalSetIsDeleteDialogOpen || setLocalIsDeleteDialogOpen;
  const isEditDialogOpen = externalIsEditDialogOpen !== undefined ? externalIsEditDialogOpen : localIsEditDialogOpen;
  const setIsEditDialogOpen = externalSetIsEditDialogOpen || setLocalIsEditDialogOpen;
  
  const [editedData, setEditedData] = useState({
    rate: scenario.rate.toString(),
    country_list: scenario.country_list,
    hts_codes: scenario.hts_codes,
    dateRange: scenario.dateRange,
  });
  const [deleteScenario, { isLoading: isDeleting }] = useDeleteScenarioMutation();
  const [updateScenario, { isLoading: isUpdating }] = useUpdateScenarioMutation();

  // For client accounts, use the user's own id as the clientId (backend expects client/user ObjectId)
  const isClientAccount = user?.userRole === roles.TDCLIENT || user?.userRole === roles.SPCLIENT;
  
  // Get the clientId from the scenario, user's _id, or selectedClient
  const clientId = scenario.clientId || (isClientAccount ? user?._id : selectedClient?._id);

  // Reset edited data when scenario changes or dialog opens
  useEffect(() => {
    if (isEditDialogOpen) {
      // Ensure we have a valid date range, using defaults if not provided
      const defaultDateRange = {
        start: scenario.dateRange?.start || "2000-01-01T00:00:00.000Z",
        end: scenario.dateRange?.end || new Date().toISOString(),
      };
      
      setEditedData({
        rate: scenario.rate.toString(),
        country_list: scenario.country_list,
        hts_codes: scenario.hts_codes,
        dateRange: scenario.dateRange || defaultDateRange,
      });
    }
  }, [scenario, isEditDialogOpen]);

  // Fetch country and HTS data for edit form
  const { data: countrySummary, isLoading: isLoadingCountries } = useGetCountrySummaryQuery(
    { 
      clientId: clientId || "",
      dateFrom: editedData.dateRange?.start ? new Date(editedData.dateRange.start).toISOString().split('T')[0] : undefined,
      dateTo: editedData.dateRange?.end ? new Date(editedData.dateRange.end).toISOString().split('T')[0] : undefined
    },
    { skip: !clientId || !isEditDialogOpen }
  );

  // Extract all available country codes
  const allCountryCodes = useMemo(() => {
    if (!countrySummary?.getCountryOriginSummary) return [];
    return countrySummary.getCountryOriginSummary.map(country => country.country_code);
  }, [countrySummary]);

  const { data: htsCodes, isLoading: isLoadingHTS } = useGetHTSCodesQuery(
    { 
      clientId: clientId || "",
      country_codes: editedData.country_list?.length ? editedData.country_list : allCountryCodes,
      dateFrom: editedData.dateRange?.start ? new Date(editedData.dateRange.start).toISOString().split('T')[0] : undefined,
      dateTo: editedData.dateRange?.end ? new Date(editedData.dateRange.end).toISOString().split('T')[0] : undefined
    },
    { skip: !clientId || !isEditDialogOpen }
  );

  const sortedCountries = useMemo(() => {
    if (!countrySummary?.getCountryOriginSummary) return [];
    
    const totalValue = countrySummary.getCountryOriginSummary.reduce(
      (sum, country) => sum + country.total_goods_value, 
      0
    );

    return [...countrySummary.getCountryOriginSummary].sort(
      (a, b) => b.total_goods_value - a.total_goods_value
    ).map(country => ({
      ...country,
      percentage: (country.total_goods_value / totalValue) * 100
    }));
  }, [countrySummary]);

  const sortedHTSCodes = useMemo(() => {
    if (!htsCodes?.getHTSCodesByCountry) return [];
    
    return [...htsCodes.getHTSCodesByCountry].sort(
      (a, b) => b.goods_value - a.goods_value
    );
  }, [htsCodes]);

  const handleDelete = async () => {
    try {
      await deleteScenario({ id: scenario._id }).unwrap();
      setIsDeleteDialogOpen(false);
      onDeleted();
    } catch (error) {
      console.error("Error deleting scenario:", error);
    }
  };

  const handleUpdate = async () => {
    try {
      // Ensure we have a valid date range for the update
      const dateRangeToUpdate = editedData.dateRange || {
        start: "2000-01-01T00:00:00.000Z",
        end: new Date().toISOString(),
      };
      
      await updateScenario({
        id: scenario._id,
        input: {
          rate: parseFloat(editedData.rate),
          country_list: editedData.country_list,
          hts_codes: editedData.hts_codes,
          dateRange: dateRangeToUpdate,
        },
      }).unwrap();
      setIsEditDialogOpen(false);
      if (onMetricsUpdate) {
        onMetricsUpdate();
      }
    } catch (error) {
      console.error("Error updating scenario:", error);
    }
  };

  // Handle date range selection
  const handleDateRangeChange = (dates: { startDate?: Date; endDate?: Date }) => {
    // When date range changes, we need to update the editedData
    const newDateRange = {
      start: dates.startDate ? dates.startDate.toISOString() : "2000-01-01T00:00:00.000Z",
      end: dates.endDate ? dates.endDate.toISOString() : new Date().toISOString(),
    };
    
    // Update the date range without resetting country and HTS selections
    setEditedData(prev => ({
      ...prev,
      dateRange: newDateRange
    }));
  };

  // Convert ISO string dates to Date objects for DateRangeFilter
  const dateRangeValue = useMemo(() => {
    return {
      startDate: editedData.dateRange?.start ? new Date(editedData.dateRange.start) : undefined,
      endDate: editedData.dateRange?.end ? new Date(editedData.dateRange.end) : undefined,
    };
  }, [editedData.dateRange]);

  // Log the date range for debugging
  useEffect(() => {
    console.log("Current date range:", editedData.dateRange);
    console.log("Converted date range value:", dateRangeValue);
  }, [editedData.dateRange, dateRangeValue]);

  // Function to check if a date is 12/31/1999
  const isDefaultStartDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.getFullYear() === 1999 && 
           date.getMonth() === 11 && // December is 11 (0-indexed)
           date.getDate() === 31;
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <Box id={id}>
      {/* Overview Card */}
      {!showOnlyRecommendations && (
        <Paper
          sx={{
            p: isSlim ? 1.75 : isCompact ? 3 : 4,
            borderRadius: "12px",
            border: `1px solid ${tokens.border.default}`,
            mb: isSlim ? 1.5 : isCompact ? 3 : 4,
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: tokens.fontWeight.bold, color: tokens.text.primary, mb: isSlim ? 0.75 : isCompact ? 1.5 : 2, fontSize: isSlim ? "1.15rem" : isCompact ? "1.5rem" : "2rem" }}>
                Duty Increase
              </Typography>
              {isLoadingMetrics ? (
                <CircularProgress size={isSlim ? 20 : 24} />
              ) : metrics ? (
                <Typography variant="h2" sx={{ color: tokens.status.danger, fontWeight: tokens.fontWeight.bold, mb: isSlim ? 0.75 : isCompact ? 2 : 3, fontSize: isSlim ? "1.9rem" : isCompact ? "2.5rem" : "3.2rem" }}>
                  {formatCurrency(metrics.dutyAmount)}
                </Typography>
              ) : null}
              <Typography variant="h6" sx={{ color: tokens.text.primary, mb: isSlim ? 0.75 : isCompact ? 1.5 : 2, fontSize: isSlim ? "0.85rem" : isCompact ? "1.1rem" : "1.3rem" }}>
                Rate: <span style={{ color: tokens.status.danger }}>{scenario.rate}%</span> | Countries: {scenario.country_list.join(", ")}
              </Typography>
              {scenario.hts_codes.length > 0 && (
                <Box sx={{ mt: isSlim ? 0.5 : isCompact ? 1 : 2 }}>
                  <Typography variant="h6" sx={{ color: tokens.text.primary, fontSize: isSlim ? "0.85rem" : isCompact ? "1.1rem" : "1.3rem" }}>
                    HTS Codes: {scenario.hts_codes.join(", ")}
                  </Typography>
                </Box>
              )}
              {scenario.dateRange && (
                <Box sx={{ mt: isSlim ? 0.5 : isCompact ? 1 : 2 }}>
                  <Typography variant="h6" sx={{ color: tokens.text.primary, fontSize: isSlim ? "0.85rem" : isCompact ? "1.1rem" : "1.3rem" }}>
                    Date Range: {isDefaultStartDate(scenario.dateRange.start) ? 
                      "All Dates" : 
                      `${new Date(scenario.dateRange.start).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })} - ${new Date(scenario.dateRange.end).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`
                    }
                  </Typography>
                </Box>
              )}
            </Box>
            <Box sx={{ display: "flex", gap: 0.5 }}>
              {!hideButtons && (
                <>
                  <IconButton onClick={() => setIsEditDialogOpen(true)} sx={{ color: tokens.text.excerpt, padding: isSlim ? 0.5 : isCompact ? 1 : 1.5 }} data-action="edit">
                    <img src={EditIcon} alt="Edit" style={{ width: isSlim ? 18 : isCompact ? 24 : 28, height: isSlim ? 18 : isCompact ? 24 : 28 }} />
                  </IconButton>
                  <IconButton onClick={() => setIsDeleteDialogOpen(true)} sx={{ color: tokens.text.excerpt, padding: isSlim ? 0.5 : isCompact ? 1 : 1.5 }} data-action="delete">
                    <img src={DeleteIcon} alt="Delete" style={{ width: isSlim ? 18 : isCompact ? 24 : 28, height: isSlim ? 18 : isCompact ? 24 : 28 }} />
                  </IconButton>
                  <IconButton onClick={onClose} sx={{ color: tokens.text.excerpt, padding: isSlim ? 0.5 : isCompact ? 1 : 1.5 }} data-action="close">
                    <CloseIcon sx={{ fontSize: isSlim ? 18 : isCompact ? 24 : 28 }} />
                  </IconButton>
                </>
              )}
            </Box>
          </Box>
        </Paper>
      )}

      {/* Recommendations Section */}
      <Box sx={{ mb: isSlim ? 1 : isCompact ? 1.5 : 4 }}>
        <Typography sx={{ fontWeight: tokens.fontWeight.bold, color: tokens.text.primary, fontSize: isSlim ? "1rem" : isCompact ? tokens.fontSize.lg : "2rem" }}>
          Recommendations to Lower Duty Impact
        </Typography>
      </Box>

      {/* Recommendation Cards */}
      <Grid container spacing={isSlim ? 1 : isCompact ? 1.5 : 4}>
        {/* Lower Your Value */}
        <Grid item xs={12} md={4}>
          <RecommendationCard title="Lower Your Value" emoji="💰" isCompact={isCompact} isSlim={isSlim}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: isSlim ? 0.5 : isCompact ? 1 : 3 }}>
              <Box>
                <Typography variant="h6" sx={{ color: tokens.text.primary, mb: 0.5, fontSize: isSlim ? "0.75rem" : isCompact ? tokens.fontSize.sm2 : "1.2rem", fontWeight: tokens.fontWeight.bold }}>
                  💱 First Sale
                </Typography>
                <Typography variant="h6" sx={{ mb: isSlim ? 0.25 : isCompact ? 0.5 : 2, color: tokens.text.excerpt, fontSize: isSlim ? "0.7rem" : isCompact ? tokens.fontSize.base : "1.2rem", lineHeight: isSlim ? 1.2 : isCompact ? 1.35 : 1.5 }}>
                  First Sale reduces dutiable value by using the earlier sale price in a multi-tiered transaction.
                </Typography>
                <Box 
                  component="a" 
                  onClick={() => navigate('/first-sale')}
                  sx={{ 
                    color: tokens.brand.primary, 
                    fontWeight: tokens.fontWeight.semibold,
                    fontSize: isSlim ? "0.7rem" : isCompact ? tokens.fontSize.base : "1.2rem",
                    cursor: 'pointer',
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'underline'
                    }
                  }}
                >
                  Explore First Sale →
                </Box>
              </Box>
              <Box>
                <Typography variant="h6" sx={{ color: tokens.text.primary, mb: 0.5, fontSize: isSlim ? "0.75rem" : isCompact ? tokens.fontSize.sm2 : "1.2rem", fontWeight: tokens.fontWeight.bold }}>
                  📦 Value Unbundling
                </Typography>
                <Typography variant="h6" sx={{ mb: isSlim ? 0.25 : isCompact ? 0.5 : 2, color: tokens.text.excerpt, fontSize: isSlim ? "0.7rem" : isCompact ? tokens.fontSize.base : "1.2rem", lineHeight: isSlim ? 1.2 : isCompact ? 1.35 : 1.5 }}>
                  Value Unbundling in order to lower value in: {scenario.country_list.join(", ")}
                </Typography>
                <Typography variant="h6" sx={{ color: "#6941C6", fontWeight: tokens.fontWeight.semibold, fontSize: isSlim ? "0.7rem" : isCompact ? tokens.fontSize.base : "1.2rem" }}>
                  Coming Soon
                </Typography>
              </Box>
            </Box>
          </RecommendationCard>
        </Grid>

        {/* Lower Your Duties */}
        <Grid item xs={12} md={4}>
          <RecommendationCard title="Lower Your Duties" emoji="📊" isCompact={isCompact} isSlim={isSlim}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: isSlim ? 0.5 : isCompact ? 1 : 3 }}>
              <Box>
                <Typography variant="h6" sx={{ color: tokens.text.primary, mb: 0.5, fontSize: isSlim ? "0.75rem" : isCompact ? tokens.fontSize.sm2 : "1.2rem", fontWeight: tokens.fontWeight.bold }}>
                  ❌ Section 301 Exclusions
                </Typography>
                <Typography variant="h6" sx={{ mb: isSlim ? 0.25 : isCompact ? 0.5 : 2, color: tokens.text.excerpt, fontSize: isSlim ? "0.7rem" : isCompact ? tokens.fontSize.base : "1.2rem", lineHeight: isSlim ? 1.2 : isCompact ? 1.35 : 1.5 }}>
                  Exclusions are government-granted exceptions that eliminate duties on specific products.
                </Typography>
                <Box 
                  component="a" 
                  onClick={() => navigate('/dashboard/potential/exclusions')}
                  sx={{ 
                    color: tokens.brand.primary, 
                    fontWeight: tokens.fontWeight.semibold,
                    fontSize: isSlim ? "0.7rem" : isCompact ? tokens.fontSize.base : "1.2rem",
                    cursor: 'pointer',
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'underline'
                    }
                  }}
                >
                  Explore Exclusions →
                </Box>
              </Box>
              <Box>
                <Typography variant="h6" sx={{ color: tokens.text.primary, mb: 0.5, fontSize: isSlim ? "0.75rem" : isCompact ? tokens.fontSize.sm2 : "1.2rem", fontWeight: tokens.fontWeight.bold }}>
                  🔄 Reclassification
                </Typography>
                <Typography variant="h6" sx={{ mb: isSlim ? 0.25 : isCompact ? 0.5 : 2, color: tokens.text.excerpt, fontSize: isSlim ? "0.7rem" : isCompact ? tokens.fontSize.base : "1.2rem", lineHeight: isSlim ? 1.2 : isCompact ? 1.35 : 1.5 }}>
                  Reclassification & Engineering change the duty rate of your product via new classification of goods.
                </Typography>
                <Typography variant="h6" sx={{ color: "#6941C6", fontWeight: tokens.fontWeight.semibold, fontSize: isSlim ? "0.7rem" : isCompact ? tokens.fontSize.base : "1.2rem" }}>
                  Coming Soon
                </Typography>
              </Box>
            </Box>
          </RecommendationCard>
        </Grid>

        {/* Delay Your Duties */}
        <Grid item xs={12} md={4}>
          <RecommendationCard title="Delay Your Duties" emoji="⏰" isCompact={isCompact} isSlim={isSlim}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: isSlim ? 0.5 : isCompact ? 1 : 3 }}>
              <Box>
                <Typography variant="h6" sx={{ color: tokens.text.primary, mb: 0.5, fontSize: isSlim ? "0.75rem" : isCompact ? tokens.fontSize.sm2 : "1.2rem", fontWeight: tokens.fontWeight.bold }}>
                  ⛴️ Foreign Trade Zones
                </Typography>
                <Typography variant="h6" sx={{ mb: isSlim ? 0.25 : isCompact ? 0.5 : 2, color: tokens.text.excerpt, fontSize: isSlim ? "0.7rem" : isCompact ? tokens.fontSize.base : "1.2rem", lineHeight: isSlim ? 1.2 : isCompact ? 1.35 : 1.5 }}>
                  Foreign Trade Zones allow you to defer, reduce, or eliminate duties by storing and manufacturing in special zones.
                </Typography>
                <Box 
                  component="a" 
                  onClick={() => navigate('/dashboard/potential/ftz')}
                  sx={{ 
                    color: tokens.brand.primary, 
                    fontWeight: tokens.fontWeight.semibold,
                    fontSize: isSlim ? "0.7rem" : isCompact ? tokens.fontSize.base : "1.2rem",
                    cursor: 'pointer',
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'underline'
                    }
                  }}
                >
                  Discover FTZ Benefits →
                </Box>
              </Box>
            </Box>
          </RecommendationCard>
        </Grid>
      </Grid>

      {/* Edit Dialog */}
      <Dialog
        open={isEditDialogOpen}
        onClose={() => setIsEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: "12px" },
        }}
      >
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: tokens.fontWeight.bold, color: tokens.text.primary, mb: 3 }}>
            Edit Scenario
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              label="Rate (%)"
              type="number"
              value={editedData.rate}
              onChange={(e) => setEditedData(prev => ({ ...prev, rate: e.target.value }))}
              required
              fullWidth
              inputProps={{ step: "0.1" }}
            />

            <Autocomplete
              disableCloseOnSelect
              multiple
              options={sortedCountries}
              loading={isLoadingCountries}
              getOptionLabel={(option) => 
                `${option.country_code} (${option.percentage.toFixed(1)}% of goods value)`
              }
              value={sortedCountries.filter(c => 
                editedData.country_list?.includes(c.country_code)
              )}
              onChange={(_, newValue) => {
                setEditedData(prev => ({
                  ...prev,
                  country_list: newValue.map(v => v.country_code),
                }));
              }}
              renderOption={(props, option) => (
                <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span style={{ fontSize: FLAG_EMOJI_SIZE }}>{getCountryFlag(option.country_code)}</span>
                  <span>{option.country_code}</span>
                  <Typography variant="caption" sx={{ color: tokens.text.secondary }}>
                    ({option.percentage.toFixed(1)}% of goods value)
                  </Typography>
                </Box>
              )}
              renderInput={(params) => {
                const updatedParams = {
                  ...params,
                  inputProps: {
                    ...params.inputProps,
                    placeholder: editedData.country_list?.length ? "" : "All Countries",
                  }
                };
                return (
                  <TextField
                    {...updatedParams}
                    label="All Countries"
                    helperText="Select countries sorted by goods value"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {isLoadingCountries ? <CircularProgress size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                );
              }}
              renderTags={(value, getTagProps) =>
                value.length === 0 ? (
                  <Box sx={{ color: tokens.text.secondary, pl: 1 }}>All Countries</Box>
                ) : (
                  value.map((option, index) => (
                    <Chip
                      icon={<span style={{ fontSize: FLAG_EMOJI_SIZE, marginLeft: '4px' }}>{getCountryFlag(option.country_code)}</span>}
                      label={`${option.country_code} (${option.percentage.toFixed(1)}%)`}
                      {...getTagProps({ index })}
                      sx={{
                        backgroundColor: tokens.bg.hover,
                        color: tokens.text.excerpt,
                        '& .MuiChip-icon': {
                          fontSize: '20px',
                        }
                      }}
                    />
                  ))
                )
              }
            />

            <Autocomplete
              multiple
              disableCloseOnSelect
              options={sortedHTSCodes}
              loading={isLoadingHTS}
              getOptionLabel={(option) => 
                `${option.hts_code} ($${(option.goods_value / 1000000).toFixed(1)}M goods value)`
              }
              value={sortedHTSCodes.filter(c => 
                editedData.hts_codes?.includes(c.hts_code)
              )}
              onChange={(_, newValue) => {
                setEditedData(prev => ({
                  ...prev,
                  hts_codes: newValue.map(v => v.hts_code),
                }));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="All HTS Codes"
                  helperText="Select HTS codes sorted by goods value, or paste multiple codes (comma, space, or newline separated)"
                  onPaste={(event) => {
                    event.preventDefault();
                    const pastedText = event.clipboardData.getData('text');
                    
                    // First split by newlines, then by other delimiters if needed
                    const newCodes = pastedText
                      .split(/\r?\n/) // Split by newlines (handles both \n and \r\n)
                      .map(line => line.trim())
                      .filter(line => line.length > 0)
                      .map(line => line.split(/[,\s]+/)) // Split each line by commas or spaces if they exist
                      .flat()
                      .filter(code => code.length > 0)
                      .map(code => code.replace(/\D/g, ""))
                      .filter(code => code.length >= 4);

                    if (newCodes.length > 0) {
                      // Combine existing and new codes, removing duplicates
                      const existingCodes = editedData.hts_codes || [];
                      const allCodes = [...new Set([...existingCodes, ...newCodes])];
                      
                      setEditedData(prev => ({
                        ...prev,
                        hts_codes: allCodes,
                      }));
                    }
                  }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isLoadingHTS ? <CircularProgress size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.length === 0 ? (
                  <Box sx={{ color: tokens.text.secondary, pl: 1 }}>All Codes</Box>
                ) : (
                  value.map((option, index) => (
                    <Chip
                      label={`${option.hts_code} ($${(option.goods_value / 1000000).toFixed(1)}M)`}
                      {...getTagProps({ index })}
                      sx={{
                        backgroundColor: tokens.bg.hover,
                        color: tokens.text.excerpt,
                      }}
                    />
                  ))
                )
              }
            />

            <Box sx={{ mt: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Date Range</Typography>
              <DateRangeFilter 
                value={dateRangeValue}
                onSelectDate={handleDateRangeChange}
              />
              {(isLoadingCountries || isLoadingHTS) && (
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <CircularProgress size={16} sx={{ mr: 1 }} />
                  <Typography variant="caption" sx={{ color: tokens.text.secondary }}>
                    Updating data based on selected date range...
                  </Typography>
                </Box>
              )}
              {dateRangeValue.startDate && dateRangeValue.endDate && (
                <Typography variant="caption" sx={{ color: tokens.text.secondary, display: 'block', mt: 1 }}>
                  Selected: {dateRangeValue.startDate.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })} - {dateRangeValue.endDate.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                </Typography>
              )}
            </Box>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 3 }}>
            <Button
              onClick={() => setIsEditDialogOpen(false)}
              sx={{ color: tokens.text.excerpt }}
              disabled={isUpdating}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleUpdate}
              disabled={isUpdating}
              sx={{
                backgroundColor: tokens.brand.primary,
                color: tokens.bg.surface,
                "&:hover": { backgroundColor: tokens.brand.primaryHover },
              }}
            >
              {isUpdating ? <CircularProgress size={24} /> : "Save Changes"}
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: "12px" },
        }}
      >
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: tokens.fontWeight.bold, color: tokens.text.primary, mb: 2 }}>
            Delete Scenario
          </Typography>
          <Typography variant="body1" sx={{ color: tokens.text.secondary, mb: 3 }}>
            Are you sure you want to delete this scenario? This action cannot be undone.
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <Button
              onClick={() => setIsDeleteDialogOpen(false)}
              sx={{ color: tokens.text.excerpt }}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleDelete}
              disabled={isDeleting}
              sx={{
                backgroundColor: tokens.status.danger,
                color: tokens.bg.surface,
                "&:hover": { backgroundColor: tokens.status.dangerHover },
              }}
            >
              {isDeleting ? <CircularProgress size={24} /> : "Delete"}
            </Button>
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
};

export default ScenarioDetail; 